import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../services/api";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";

/* App usage monitoring, browser side.
 *
 * MonitoringProvider asks the server once per sign-in (/monitoring/me) whether
 * this person is tracked and whether they may open the Usage Monitor page.
 * When tracked, <ActivityTracker> sends `mon:beat` over the socket:
 *   • immediately when the section (dashboard tab) or the state changes,
 *   • otherwise every heartbeatSec, so the server can extend the open segment.
 * The server's clock measures every duration — the browser only reports what
 * it sees right now. Nothing is sent until the notice has been acknowledged. */

const MonitoringContext = createContext({ me: null, access: null, reload: () => {} });

// The persisted-tab key each dashboard uses (hooks/usePersistedTab).
const ROLE_TAB_KEY = { hr: "hr_tab", pm: "pm_tab", itadmin: "ita_tab", intern: "intern_tab", employee: "emp_tab", superadmin: "sa_tab" };

function clientId() {
  try {
    let id = sessionStorage.getItem("mon_client_id");
    if (!id) {
      const a = new Uint8Array(12);
      (window.crypto || window.msCrypto).getRandomValues(a);
      id = "c" + Array.from(a, (b) => b.toString(36).padStart(2, "0")).join("");
      sessionStorage.setItem("mon_client_id", id);
    }
    return id;
  } catch {
    return "c" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

function currentTab(user) {
  if (user?.status === "TEMP") return "onboarding";
  if (user?.status === "ONBOARDING_SUBMITTED") return "pending-verification";
  if (/^\/overview\/?$/i.test(window.location.pathname)) return "company-overview";
  const t = window.__amsTab;
  return t && t.key === ROLE_TAB_KEY[user?.role] ? t.tab : "dashboard";
}

function ActivityTracker({ user, config }) {
  const { socket, connected } = useSocket();
  const idleMs = (config.idleThresholdSec || 300) * 1000;
  const beatMs = (config.heartbeatSec || 30) * 1000;
  const ref = useRef({ lastInput: Date.now(), lastSent: 0, state: null, tab: null });
  const id = useRef(clientId()).current;
  // The user object is replaced on every /auth/me refresh; only these fields
  // matter here, so a refresh must not restart the session.
  const userRef = useRef(user);
  userRef.current = user;
  const userKey = `${user?.userId}|${user?.role}|${user?.status}`;

  useEffect(() => {
    if (!socket || !connected) return undefined;
    const s = ref.current;

    const read = () => {
      if (document.hidden || !document.hasFocus()) return "away";
      return Date.now() - s.lastInput > idleMs ? "idle" : "active";
    };
    const send = (force) => {
      if (!socket.connected) return; // socket.io would queue it and replay it late
      const state = read();
      const tab = currentTab(userRef.current);
      const changed = state !== s.state || tab !== s.tab;
      if (!force && !changed && Date.now() - s.lastSent < beatMs) return;
      s.state = state;
      s.tab = tab;
      s.lastSent = Date.now();
      socket.emit("mon:beat", { clientId: id, state, tab });
    };

    // Input only needs to be noticed, not counted — one timestamp is enough.
    let lastMove = 0;
    const onInput = (e) => {
      const now = Date.now();
      if (e.type === "mousemove" && now - lastMove < 1000) return;
      lastMove = now;
      const wasIdle = now - s.lastInput > idleMs;
      s.lastInput = now;
      if (wasIdle) send(false);
    };
    const onChange = () => send(false);
    const INPUT = ["mousemove", "mousedown", "keydown", "wheel", "touchstart", "scroll"];
    INPUT.forEach((ev) => window.addEventListener(ev, onInput, { passive: true, capture: true }));
    ["focus", "blur", "app:tab-change", "popstate", "app:navigate"].forEach((ev) => window.addEventListener(ev, onChange));
    document.addEventListener("visibilitychange", onChange);
    // Tab closed or reloaded: close the session now instead of waiting for
    // the server to notice the missing heartbeat.
    const onHide = () => api.endMonitoringSession(id, "closed");
    window.addEventListener("pagehide", onHide);

    send(true); // (re)connected — tell the server where we are right away
    const tick = setInterval(() => send(false), 5000);
    // Captured now: by the time this cleanup runs on sign-out the token is gone.
    const token = api.getMonitoringToken();

    return () => {
      clearInterval(tick);
      INPUT.forEach((ev) => window.removeEventListener(ev, onInput, { capture: true }));
      ["focus", "blur", "app:tab-change", "popstate", "app:navigate"].forEach((ev) => window.removeEventListener(ev, onChange));
      document.removeEventListener("visibilitychange", onChange);
      window.removeEventListener("pagehide", onHide);
      // Unmounting while still signed in = tracking was switched off;
      // with no token left = the person signed out.
      // A dropped connection also lands here; then the server's heartbeat
      // timeout closes the session, so nothing is queued for a late replay.
      if (!api.getMonitoringToken()) api.endMonitoringSession(id, "logout", token);
      else if (socket.connected) socket.emit("mon:end", { clientId: id, reason: "closed" });
      s.state = null;
    };
  }, [socket, connected, userKey, id, idleMs, beatMs]);

  return null;
}

function NoticeModal({ notice, onAccept }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const accept = async () => {
    setBusy(true);
    setErr("");
    try { await onAccept(); } catch (e) { setErr(e.message || "Could not save. Please try again."); setBusy(false); }
  };
  return createPortal(
    <div role="dialog" aria-modal="true" aria-labelledby="mon-notice-title"
      style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "var(--n-0)", color: "var(--n-900)", borderRadius: 14, maxWidth: 520, width: "100%", padding: "24px 24px 20px", boxShadow: "var(--shadow-lg)", border: "1px solid var(--n-200)" }}>
        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--n-500)", marginBottom: 6 }}>Company notice</div>
        <h2 id="mon-notice-title" style={{ margin: "0 0 12px", fontSize: 19, fontWeight: 800 }}>{notice.title}</h2>
        <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "var(--n-700)", whiteSpace: "pre-wrap", maxHeight: "50vh", overflowY: "auto" }}>{notice.body}</div>
        {err && <div style={{ marginTop: 12, color: "var(--danger-500)", fontSize: 12.5, fontWeight: 600 }}>{err}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={accept} disabled={busy}
            style={{ background: "var(--brand-500)", color: "#fff", border: "none", borderRadius: 9, padding: "10px 18px", fontWeight: 700, fontSize: 13, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Saving…" : "I have read and understood this"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function MonitoringProvider({ children }) {
  const { user } = useAuth();
  const { socket, connected } = useSocket();
  const [me, setMe] = useState(null);

  // Blocked sessions (2FA setup pending) must not call protected APIs: a 403
  // there makes api.js reload the page.
  const eligible = !!user && !(user.role !== "superadmin" && user.two_factor_setup_required === true);

  const reload = useCallback(() => {
    if (!eligible) { setMe(null); return Promise.resolve(); }
    return api.getMonitoringMe().then((d) => setMe(d && d.tracking ? d : null)).catch(() => setMe(null));
  }, [eligible]);

  useEffect(() => { reload(); }, [reload, user?.userId, user?.role]);

  // A superadmin changed settings / who is tracked / who can view.
  useEffect(() => {
    if (!socket || !connected) return undefined;
    const onChange = () => reload();
    socket.on("mon:config-changed", onChange);
    return () => socket.off("mon:config-changed", onChange);
  }, [socket, connected, reload]);

  const tracked = !!(eligible && me?.tracking?.enabled);
  const needsNotice = tracked && !!me?.notice?.required;

  const accept = async () => {
    await api.acceptMonitoringNotice(me.notice.version);
    await reload();
  };

  return (
    <MonitoringContext.Provider value={{ me, access: me?.access || null, reload }}>
      {children}
      {tracked && !needsNotice && <ActivityTracker user={user} config={me.tracking} />}
      {needsNotice && <NoticeModal notice={me.notice} onAccept={accept} />}
    </MonitoringContext.Provider>
  );
}

export const useMonitoring = () => useContext(MonitoringContext);

// For dashboards: whether to show the "Usage Monitor" section at all.
export function useMonitoringAccess() {
  const { access } = useContext(MonitoringContext);
  return access || { canView: false };
}
